import { Box, Button, Flex, Heading, Text } from '@radix-ui/themes'
import { ReactNode } from 'react'
import ExpandableCards from '@/shared/manager/ExpandableCards'

export interface BrowseGroup<T> {
  key: string
  /** Shown above the group; the first group usually has none. */
  title?: string
  muted?: boolean
  items: T[]
}

interface Props<T> {
  /** Already filtered — the browser only lays them out. */
  groups: BrowseGroup<T>[]
  /** Before filtering, for "Showing 12 of 64". */
  total: number
  /** The browser's own controls. Left out on a short list (see showFilters). */
  filters?: ReactNode
  active: boolean
  onClear: () => void
  /** "No cars match." */
  noMatch: string
  getKey: (item: T) => number
  renderSummary: (item: T) => ReactNode
  renderDetail: (item: T) => ReactNode
}

// Below this, a filter bar is more to read than the list it filters.
const FILTER_THRESHOLD = 8

export const showFilters = (total: number) => total > FILTER_THRESHOLD

/**
 * The frame CarBrowser and MediaBrowser share: a sticky filter bar over one or
 * more groups of cards that open in place. In place rather than a dialog: a
 * dialog has no history entry, so on a phone the back gesture would leave the
 * page instead of closing it — see ExpandableCards.
 *
 * Several groups share one set of filters ("Was there" and "Said they were
 * going"), so a filter never has to be set twice.
 */
export default function CardBrowser<T>({
  groups,
  total,
  filters,
  active,
  onClear,
  noMatch,
  getKey,
  renderSummary,
  renderDetail,
}: Props<T>) {
  const shown = groups.reduce((sum, g) => sum + g.items.length, 0)

  return (
    <Flex direction="column" gap="4">
      {filters && (
        <Box
          py="3"
          // Bleeds 8px past the list on each side: an open card's accent ring is
          // drawn outside its edges, and would otherwise show either side of
          // the bar as the card scrolls under it.
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            marginInline: -8,
            paddingInline: 8,
            background: 'var(--color-background)',
            borderBottom: '1px solid var(--gray-5)',
          }}
        >
          <Flex direction="column" gap="3">
            {filters}
            <Flex align="center" gap="3">
              <Text size="2" color="gray">
                Showing {shown} of {total}
              </Text>
              {active && (
                <Button size="1" variant="ghost" onClick={onClear}>
                  Clear filters
                </Button>
              )}
            </Flex>
          </Flex>
        </Box>
      )}

      {shown === 0 ? (
        <Flex align="center" gap="2">
          <Text color="gray">{noMatch}</Text>
          <Button size="1" variant="ghost" onClick={onClear}>
            Clear filters
          </Button>
        </Flex>
      ) : (
        groups
          .filter((group) => group.items.length > 0)
          .map((group) => (
            <Box key={group.key} style={group.muted ? { opacity: 0.6 } : undefined}>
              {group.title && (
                <Heading size="4" mb="3" color="gray">
                  {group.title}
                </Heading>
              )}
              <ExpandableCards
                items={group.items}
                getKey={getKey}
                renderSummary={renderSummary}
                renderDetail={renderDetail}
                emptyMessage=""
                columns={2}
              />
            </Box>
          ))
      )}
    </Flex>
  )
}

/** One toggleable chip in a filter bar's facet row ("● White 7"). */
export function FacetChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Button
      size="1"
      radius="full"
      variant={on ? 'solid' : 'soft'}
      color={on ? undefined : 'gray'}
      onClick={onClick}
      aria-pressed={on}
    >
      {children}
    </Button>
  )
}

/** A collapsed card's square thumbnail, or `fallback` (an icon) on gray when there's no image. */
export function CardThumb({ src, fallback }: { src: string | null; fallback: ReactNode }) {
  const box = { width: 72, height: 72, borderRadius: 8, flexShrink: 0 } as const
  return src ? (
    <img src={src} alt="" loading="lazy" style={{ ...box, objectFit: 'cover', background: 'var(--gray-4)' }} />
  ) : (
    <Flex align="center" justify="center" style={{ ...box, background: 'var(--gray-3)' }}>
      {fallback}
    </Flex>
  )
}

/** Toggles `value` in or out of a list of chosen facet keys. */
export const toggled = (values: string[], value: string) =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
